/**
 * BlockFallback — reusable inline fallback UI for each block type.
 * Used inside SectionErrorBoundary and query error states.
 */
import { RefreshCw, WifiOff, Lock, FileQuestion, AlertTriangle, MessageSquare, Camera, List, Gift, Image } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ErrorCategory } from "@/lib/errorClassifier";

export type BlockType =
  | "recipe"
  | "bonus"
  | "banner"
  | "chat"
  | "glucophoto"
  | "creator"
  | "list"
  | "section"
  | "generic";

interface Props {
  blockType?: BlockType;
  category?: ErrorCategory;
  onRetry?: () => void;
  compact?: boolean;
  /** Extra context for telemetry */
  context?: string;
}

const BLOCK_ICONS: Record<BlockType, React.ElementType> = {
  recipe:     AlertTriangle,
  bonus:      Gift,
  banner:     Image,
  chat:       MessageSquare,
  glucophoto: Camera,
  creator:    RefreshCw,
  list:       List,
  section:    AlertTriangle,
  generic:    AlertTriangle,
};

const CATEGORY_ICONS: Record<ErrorCategory, React.ElementType> = {
  network:          WifiOff,
  timeout:          WifiOff,
  parsing:          FileQuestion,
  access:           Lock,
  missing_resource: FileQuestion,
  unexpected:       AlertTriangle,
};

export default function BlockFallback({ blockType = "generic", category, onRetry, compact = false, context }: Props) {
  const { t } = useTranslation();

  const Icon = category ? CATEGORY_ICONS[category] : BLOCK_ICONS[blockType];

  // Human-friendly messages per category
  const categoryMessages: Record<ErrorCategory, { title: string; desc: string }> = {
    network:          { title: t("errors.network_title"),   desc: t("errors.network_desc") },
    timeout:          { title: t("errors.timeout_title"),   desc: t("errors.timeout_desc") },
    parsing:          { title: t("errors.parsing_title"),   desc: t("errors.parsing_desc") },
    access:           { title: t("errors.access_title"),    desc: t("errors.access_desc") },
    missing_resource: { title: t("errors.missing_title"),   desc: t("errors.missing_desc") },
    unexpected:       { title: t("errors.unexpected_title"), desc: t("errors.unexpected_desc") },
  };

  // Block-type fallback messages (no category)
  const blockMessages: Record<BlockType, { title: string; desc: string }> = {
    recipe:     { title: t("errors.recipe_title"),     desc: t("errors.recipe_desc") },
    bonus:      { title: t("errors.bonus_title"),      desc: t("errors.bonus_desc") },
    banner:     { title: t("errors.banner_title"),     desc: t("errors.banner_desc") },
    chat:       { title: t("errors.chat_title"),       desc: t("errors.chat_desc") },
    glucophoto: { title: t("errors.glucophoto_title"), desc: t("errors.glucophoto_desc") },
    creator:    { title: t("errors.creator_title"),    desc: t("errors.creator_desc") },
    list:       { title: t("errors.list_title"),       desc: t("errors.list_desc") },
    section:    { title: t("errors.section_title"),    desc: t("errors.section_desc") },
    generic:    { title: t("errors.unexpected_title"), desc: t("errors.unexpected_desc") },
  };

  const { title, desc } = category ? categoryMessages[category] : blockMessages[blockType];

  const handleRetry = () => {
    onRetry?.();
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border/40 bg-muted/30">
        <Icon size={14} className="text-muted-foreground shrink-0" />
        <p className="text-xs text-muted-foreground flex-1">{title}</p>
        {onRetry && (
          <button onClick={handleRetry} className="shrink-0 text-[11px] font-semibold text-primary hover:underline">
            {t("errors.retry")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border/40 bg-muted/20 p-5 text-center space-y-2">
      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto">
        <Icon size={18} className="text-muted-foreground" />
      </div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
      {onRetry && (
        <button
          onClick={handleRetry}
          className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold text-primary hover:underline active:scale-95 transition-transform"
        >
          <RefreshCw size={12} />
          {t("errors.retry")}
        </button>
      )}
    </div>
  );
}
